import React, { useEffect, useMemo, useState } from 'react';
import { graphql } from 'gatsby';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import { config } from '@keytrade/functions';
import { CheckboxGroup } from '@keytrade/components-checkedinput';

import Title from '@/components/Title';
import Paragraph from '@/components/Paragraph';
import BlockWrapper from '@/components/BlockWrapper';
import Container from '@/components/Container';
import { mediaQuery } from '@/utils/helpers';
import useTranslations from '@/hooks/useTranslations';
import translationsKeys from '@/intl/en.json';

import Vacancy, { VacancyType } from './Vacancy';
import MobileJobFilters from './MobileJobFilters';
import FilterPill from '../../filters/FilterPill';

const { teams, levels, contracts } = translationsKeys.jobs;
const teamsKeys = Object.keys(teams);
const levelsKeys = Object.keys(levels);
const contractKeys = Object.keys(contracts);

const { list: colors } = config.colors;
const { getFont } = config.fonts;

const JobAnchor = styled.a`
  position: relative;
  top: 150px;
`;

const SubtitleContainer = styled.div`
  display: block;
  max-width: 744px;
  margin: 0 auto;
  text-align: center;
  margin-bottom: 72px;
`;

const FilterContainer = styled.div`
  display: none;
  width: 100%;
  max-width: 264px;
  margin-right: 24px;
  ${mediaQuery.md(`
    display: block;
  `)}
`;

const JobListContainer = styled.div`
  width: 100%;
  ${mediaQuery.md(`
    width: 100%;
  `)}
`;

const VacancyWrapper = styled.div`
  display: flex;
`;

const FilterBlock = styled.div`
  ${mediaQuery.md(`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    border-radius: 12px;
    background-color: ${colors.BlueLight};
    padding: 17px 24px;
    margin-bottom: 20px;
  `)}
`;

const FilterPills = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  max-width: 80%;
`;

const TotalAmount = styled.span`
  display: none;
  font-size: 14px;
  line-height: 20px;
  text-align: right;
  font-family: ${({ theme }) => getFont(theme, 'regular')};
  color: ${colors.BlueMoon};
  ${mediaQuery.md(`
    display: block;
  `)};
`;

const ClearFilters = styled.span`
  display: none;
  font-size: 14px;
  line-height: 20px;
  font-family: ${({ theme }) => getFont(theme, 'regular')};
  color: ${colors.BlueDawn};
  text-decoration: none;
  cursor: pointer;
  ${mediaQuery.md(`
    display: block;
  `)};
`;
const NoResult = styled.span`
  display: block;
  text-align: center;
  font-size: 14px;
  line-height: 20px;
  font-family: ${({ theme }) => getFont(theme, 'regular')};
  color: ${colors.BlueMoon};
`;
const JobListBlock = styled.div``;

export type JobListingBlockType = {
  __typename: string;
  contentful_id: string;
  id: string;
  title: string;
  node_locale: string;
  subtitleString: {
    subtitle: string;
  };
  allVacancies: {
    nodes: Array<VacancyType>;
  };
};

const JobListingBlock: React.FC<JobListingBlockType> = ({
  id,
  title,
  subtitleString,
  allVacancies,
  node_locale,
}) => {
  const { t, to } = useTranslations();

  const [teamFilters, setTeamFilters] = useState<string[]>([]);
  const [levelFilters, setLevelFilters] = useState<string[]>([]);
  const [contractTypeFilters, setContractTypeFilters] = useState<string[]>([]);
  const [hasActiveFilters, setActiveFilters] = useState(false);
  const [filteredArray, setFilteredArray] = useState(
    allVacancies.nodes || null,
  );

  const sortByValue = (a, b) => parseInt(a.value) - parseInt(b.value);

  const { teams, levels, contracts } = useMemo(() => {
    const teams = teamsKeys
      .map((team) => to(`jobs.teams.${team}`, team))
      .sort(sortByValue);
    const levels = levelsKeys
      .map((level) => to(`jobs.levels.${level}`, level))
      .sort(sortByValue);
    const contracts = contractKeys
      .map((contract) => to(`jobs.contracts.${contract}`, contract))
      .sort(sortByValue);

    return { teams, levels, contracts };
  }, [teamsKeys, levelsKeys, contractKeys, to, sortByValue]);

  const handleFilters = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    if (!value) {
      return;
    }

    setter((prevState) =>
      prevState.includes(value)
        ? prevState.filter((item) => item !== value)
        : [...prevState, value],
    );
  };

  const handleTeamsFilter = (event) =>
    handleFilters(event.target.value, setTeamFilters);
  const handleLevelsFilter = (event) =>
    handleFilters(event.target.value, setLevelFilters);
  const handleContractTypeFilter = (event) =>
    handleFilters(event.target.value, setContractTypeFilters);

  const handleSetFilters = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setter((oldValue) => oldValue.filter((item) => item !== value));
  };

  const handleSetTeamsFilter = (value: string) =>
    handleSetFilters(value, setTeamFilters);
  const handleSetLevelsFilter = (value: string) =>
    handleSetFilters(value, setLevelFilters);
  const handleSetContractTypeFilter = (value: string) =>
    handleSetFilters(value, setContractTypeFilters);

  const updateActiveFilters = () =>
    setActiveFilters(
      !!contractTypeFilters.length ||
        !!teamFilters.length ||
        !!levelFilters.length,
    );

  useEffect(() => {
    let arr = allVacancies.nodes;

    if (contractTypeFilters.length) {
      arr = arr.filter((vacancy) =>
        contractTypeFilters.includes(vacancy.contractType),
      );
    }

    if (teamFilters.length) {
      arr = arr.filter((vacancy) => teamFilters.includes(vacancy.type));
    }

    if (levelFilters.length) {
      arr = arr.filter((vacancy) => levelFilters.includes(vacancy.category));
    }

    updateActiveFilters();
    setFilteredArray(arr);
  }, [contractTypeFilters, teamFilters, levelFilters]);

  const clearFilters = (
    filter?: undefined | 'levels' | 'teams' | 'contractTypes',
  ) => {
    switch (filter) {
      case 'teams':
        setTeamFilters([]);
        break;
      case 'levels':
        setLevelFilters([]);
        break;
      case 'contractTypes':
        setContractTypeFilters([]);
        break;
      default:
        setContractTypeFilters([]);
        setLevelFilters([]);
        setTeamFilters([]);
    }

    updateActiveFilters();
  };

  return (
    <BlockWrapper color={'White'} padding={'small'} overflow={'hidden'} id={id}>
      <JobAnchor id='jobListing' />
      <Container>
        <Title level='h2' size='xl' center margin={'0 0 16px 0'}>
          {title}
        </Title>
        <SubtitleContainer>
          <Paragraph margin={'0 0 22px 0'} color='GreyBlue'>
            {subtitleString.subtitle}
          </Paragraph>
        </SubtitleContainer>
        <MobileJobFilters
          teams={{
            content: teams,
            handleFilter: handleTeamsFilter,
            contentSelected: teamFilters,
          }}
          levels={{
            content: levels,
            handleFilter: handleLevelsFilter,
            contentSelected: levelFilters,
          }}
          contractTypes={{
            content: contracts,
            handleFilter: handleContractTypeFilter,
            contentSelected: contractTypeFilters,
          }}
          clearMobileFilters={clearFilters}
        ></MobileJobFilters>
        <VacancyWrapper>
          <FilterContainer>
            <CheckboxGroup
              name='Teams'
              label={t('jobs.teams_label')}
              options={teams}
              value={teamFilters}
              onChange={(e) => handleTeamsFilter(e)}
              errorMessage=''
              margin='0 0 42px 0'
            />
            <CheckboxGroup
              name='Levels'
              label={t('jobs.levels_label')}
              options={levels}
              value={levelFilters}
              onChange={(e) => handleLevelsFilter(e)}
              errorMessage=''
              margin='0 0 42px 0'
            />
            <CheckboxGroup
              name='ContractType'
              label={t('jobs.contractTypes_label')}
              options={contracts}
              value={contractTypeFilters}
              onChange={(e) => handleContractTypeFilter(e)}
              errorMessage=''
              margin='0 0 42px 0'
            />
          </FilterContainer>
          <JobListContainer>
            {hasActiveFilters && (
              <FilterBlock>
                <FilterPills>
                  {teamFilters.map((val) => (
                    <FilterPill
                      key={val}
                      componentType='jobs'
                      value={val}
                      type='teams'
                      removeFilter={() => handleSetTeamsFilter(val)}
                    />
                  ))}
                  {levelFilters.map((val) => (
                    <FilterPill
                      key={val}
                      componentType='jobs'
                      value={val}
                      type='levels'
                      removeFilter={() => handleSetLevelsFilter(val)}
                    />
                  ))}
                  {contractTypeFilters.map((val) => (
                    <FilterPill
                      key={val}
                      componentType='jobs'
                      value={val}
                      type='contracts'
                      removeFilter={() => handleSetContractTypeFilter(val)}
                    />
                  ))}

                  <ClearFilters
                    onClick={(e) => {
                      e.preventDefault();
                      clearFilters();
                    }}
                  >
                    {t('filter.clear_all')}
                  </ClearFilters>
                </FilterPills>
                <TotalAmount>
                  {`${filteredArray.length} ${t(
                    filteredArray?.length === 1
                      ? 'filter.matches_single'
                      : 'filter.matches',
                  )}`}
                </TotalAmount>
              </FilterBlock>
            )}
            <JobListBlock>
              <NoResult>
                {!filteredArray.length && t('jobs.no_matches')}
              </NoResult>
              {filteredArray
                ?.sort((a, b) => {
                  const dateComparison =
                    DateTime.fromISO(b.beginPublication).toSeconds() -
                    DateTime.fromISO(a.beginPublication).toSeconds();

                  if (dateComparison === 0) {
                    const titleA = a.title.toUpperCase();
                    const titleB = b.title.toUpperCase();

                    if (titleA < titleB) {
                      return -1;
                    }

                    if (titleA > titleB) {
                      return 1;
                    }

                    return 0;
                  }

                  return dateComparison;
                })
                .map((vacancy) => (
                  <Vacancy
                    {...vacancy}
                    locale={node_locale}
                    key={vacancy.code}
                  />
                ))}
            </JobListBlock>
          </JobListContainer>
        </VacancyWrapper>
      </Container>
    </BlockWrapper>
  );
};

export default JobListingBlock;
export const JobListingBlockFragmentQuery = graphql`
  fragment JobListingBlockFragment on ContentfulJobListingBlock {
    contentful_id
    id
    __typename
    node_locale
    title
    subtitleString: subtitle {
      subtitle
    }
  }
`;
